import { GA_LABEL_ENUMS, triggerGAEvent } from "../../constant/google-analytics/GAEvents";
import type { Turn, VoiceStats, VoiceSession } from "./types";
import { VoiceRuntime, now } from "./runtime";

const END_REASONS: Record<string, string> = {
  "mic-tap": "tap",
  idle: "idle",
  "left-chat": "left_chat",
  connection_lost: "dropped",
  expired: "expired",
  content: "safety",
  "start-failed": "start_failed",
  unmount: "unmount",
};

export const freshStats = (): VoiceStats => ({
  requests: 0,
  chips: 0,
  typed: 0,
  voiceOnly: 0,
  busy: 0,
  heard: 0,
  empty: 0,
  failed: 0,
  stopped: 0,
  mutes: 0,
  mutedMs: 0,
  mutedAt: null,
  cleanFailed: 0,
});

const endReason = (reason: string, loggedIn: boolean): string =>
  reason === "disabled" ? (loggedIn ? "disabled" : "logout") : END_REASONS[reason] ?? "remote";

export const micPermission = async (): Promise<string> => {
  try {
    return (await navigator.permissions.query({ name: "microphone" as PermissionName })).state;
  } catch {
    return "unknown";
  }
};

export const requestSource = (turn: Turn): string => (turn.delegation ? "spoken" : "chip");

export const createVoiceAnalytics = (rt: VoiceRuntime) => {
  const { live, sessionRef } = rt;

  const trackAnswered = (turn: Turn) => {
    if (turn.failed || turn.stopped) return;
    const timing = turn.timing;
    triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_request_answered, "", "", "", {
      source: requestSource(turn),
      case: turn.lastCase ?? "",
      ...(timing?.frameMs !== undefined ? { first_frame_ms: timing.frameMs } : {}),
      ...(timing?.speechMs !== undefined ? { first_speech_ms: timing.speechMs } : {}),
      total_ms: now() - (timing?.at ?? turn.handOffAt),
      voice_ended_first: sessionRef.current === null,
    });
  };

  const trackEnd = (session: VoiceSession, reason: string) => {
    const { stats } = session;
    const at = now();
    triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_ended, "", "", "", {
      reason: endReason(reason, !!live.current.state.userInfo?.loggedIn),
      connected: session.startedAt !== null,
      duration_s: session.startedAt === null ? 0 : Math.round((at - session.startedAt) / 1000),
      requests: stats.requests,
      chips: stats.chips,
      typed: stats.typed,
      voice_only: stats.voiceOnly,
      busy: stats.busy,
      heard: stats.heard,
      empty: stats.empty,
      failed: stats.failed,
      stopped: stats.stopped,
      mutes: stats.mutes,
      muted_s: Math.round((stats.mutedMs + (stats.mutedAt === null ? 0 : at - stats.mutedAt)) / 1000),
      clean_failed: stats.cleanFailed,
    });
  };

  const trackMute = (session: VoiceSession, mutedNow: boolean) => {
    const { stats } = session;
    if (mutedNow) {
      stats.mutes += 1;
      stats.mutedAt = now();
      triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_mute_click, "", "", "", { connecting: session.startedAt === null });
      return;
    }
    const mutedMs = stats.mutedAt === null ? 0 : now() - stats.mutedAt;
    stats.mutedMs += mutedMs;
    stats.mutedAt = null;
    triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_unmute_click, "", "", "", { muted_s: Math.round(mutedMs / 1000) });
  };

  return { trackAnswered, trackEnd, trackMute };
};

export type VoiceAnalytics = ReturnType<typeof createVoiceAnalytics>;
